import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of, timer } from 'rxjs';
import { catchError, debounce, filter, map, switchMap } from 'rxjs/operators';
import { SuppliersService } from '@emi/shared/data-access';
import * as supplierActions from './supplier-actions';
import * as dataExtractionActions from '../extraction/extraction-actions';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { SupplierState } from './supplier-reducer';
import { getSupplierState } from './supplier-selectors';
import { Filters, SupplierDto } from '@emi/shared/models';
import { DataExtractionState } from '../extraction/extraction-reducer';
import { getSelectedFilters } from '../extraction/extraction-selectors';
import { createFilterDto } from '@emi/shared/utils';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';

@Injectable()
export class SupplierEffects {
    private currentState: SupplierState;
    private currentFilters: Filters;
    private previousFilters: Filters;
    private searchTermState: string;
    private previousSearchTermState: string;
    private includeSearchPhrase: boolean | undefined;

    constructor(
        private actions$: Actions,
        private suppliersService: SuppliersService,
        private supplierStore: Store<SupplierState>,
        private store: Store<DataExtractionState>,
        private skuSearchStore: Store<SkuSearchState>
    ) {
        this.supplierStore.pipe(select(getSupplierState)).subscribe((supplierState) => (this.currentState = supplierState));
        this.skuSearchStore.pipe(select(selectSkuSearch)).subscribe((skuSearchState) => (this.searchTermState = skuSearchState.searchTerm, this.includeSearchPhrase = skuSearchState.includeSearchPhrase));
    }

    getSuppliers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(supplierActions.getSuppliers),
            map(() => {
                return supplierActions.isLoadingSuppliers();
            })
        )
    );

    getSuppliersNextPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(supplierActions.getNextPageSuppliers),
            switchMap(() => {
                return this.suppliersService
                    .getSuppliers(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState: undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                            searchText: this.currentState.searchText ?? undefined,
                        })
                    )
                    .pipe(
                        map((data: SupplierDto) => {
                            this.previousFilters = this.currentFilters;
                            const newPage = data.results;

                            return supplierActions.updateSuppliersSelectionSuccess({
                              suppliers: newPage,
                              metadata: data.metadata
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(supplierActions.getSuppliersFailure({ error }));
                        })
                    );
            })
        )
    );

    amendSelected$ = createEffect(() =>
        this.actions$.pipe(
            ofType(supplierActions.amendSupplierSelections),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));

                return this.suppliersService
                    .getSuppliers(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                        })
                    )
                    .pipe(
                        map((supplierResponse) => {
                            const amendedSuppliers = this.currentFilters.suppliers.filter(
                                (s) => !!supplierResponse.results.find((supplier) => supplier.id == s.id)
                            );
                            return dataExtractionActions.addSuppliersSelection({ suppliers: amendedSuppliers });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            if (error.status == 404) {
                                return of(dataExtractionActions.addSuppliersSelection({ suppliers: [] }));
                            }
                            return of(supplierActions.getSuppliersFailure({ error }));
                        })
                    );
            })
        )
    );

    fetchSuppliers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(supplierActions.fetchSuppliers),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));
                if (this.currentFilters.suppliers.length > 0) {
                    if (this.currentFilters === this.previousFilters && this.searchTermState === this.previousSearchTermState) {
                        return of(
                            supplierActions.getSuppliersSuccess({
                                suppliers: this.currentState.suppliers,
                                metadata: this.currentState.metadata,
                                searchText: this.currentState.searchText ?? '',
                            })
                        );
                    }
                }
                return this.suppliersService
                    .getSuppliers(
                        createFilterDto({
                          filterState: this.currentFilters,
                          searchTerm: this.includeSearchPhrase ? this.searchTermState ?? undefined: ''
                        }))
                    .pipe(
                        map((data: SupplierDto) => {
                            this.previousFilters = this.currentFilters;
                            this.previousSearchTermState = this.searchTermState;
                            return supplierActions.getSuppliersSuccess({
                                suppliers: data.results,
                                metadata: data.metadata,
                                searchText: this.currentState.searchText ?? '',
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(supplierActions.getSuppliersFailure({ error }));
                        })
                    );
            })
        )
    );

    searchSuppliers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(supplierActions.searchSuppliers),
            debounce((action) => timer(action.delay)),
            filter((action) => action.searchText !== this.currentState.searchText),
            concatLatestFrom(() => this.store.select(getSelectedFilters)),
            switchMap(([action, currentFilters]) => {
                return this.suppliersService
                    .getSuppliers(
                        createFilterDto({
                          filterState: currentFilters,
                          searchText: action.searchText,
                          searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined
                        })
                    )
                    .pipe(
                        map((data: SupplierDto) => {
                            this.previousFilters = currentFilters;
                            return supplierActions.getSuppliersSuccess({
                                suppliers: data.results,
                                metadata: data.metadata,
                                searchText: action.searchText,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(supplierActions.getSuppliersFailure({ error }));
                        })
                    );
            })
        )
    );

    setIsLoading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(supplierActions.isLoadingSuppliers),
            map(() => supplierActions.fetchSuppliers())
        )
    );
}
